import { useQuery } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";

interface SnippetProp {
  id: number;
  title: string;
  type: string;
  used_in_replies: number;
}

export type { SnippetProp };

export interface SnippetResponse {
  count: number;
  records: Array<SnippetProp>;
}

export const CUSTOM_RESOURCES_BASE_QUERY_KEY = [
  "internal_api",
  "community_bot",
  "custom_resources",
];

export const SNIPPETS_FOR_KNOWLEDGE_QUERY_KEY = [
  ...CUSTOM_RESOURCES_BASE_QUERY_KEY,
  "for_knowledge",
];

export const SNIPPETS_FOR_IMPORT_QUERY_KEY = [
  ...CUSTOM_RESOURCES_BASE_QUERY_KEY,
  "for_import",
];

export const useSnippetsForKnowledge = ({
  currentPage,
  snippetIds,
}: {
  currentPage: number;
  snippetIds: number[];
}) =>
  useQuery<SnippetResponse>({
    queryKey: [...SNIPPETS_FOR_KNOWLEDGE_QUERY_KEY, snippetIds],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.customResources.index({
          params: {
            page: currentPage,
            per_page: 10,
            snippet_ids: snippetIds.join(","),
            imported: "true",
          },
        }),
      ),
    keepPreviousData: true,
  });

export const useSnippetsForImport = ({
  currentPage,
  snippetIds,
  title,
  type,
}: {
  currentPage: number;
  snippetIds: number[];
  title?: string;
  type?: string;
}) =>
  useQuery<SnippetResponse>({
    queryKey: [...SNIPPETS_FOR_IMPORT_QUERY_KEY, snippetIds, title, type],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.customResources.index({
          params: {
            page: currentPage,
            per_page: 10,
            snippet_ids: snippetIds.join(","),
            imported: "false",
            ...(title ? { title } : {}),
            ...(type ? { type } : {}),
          },
        }),
      ),
    keepPreviousData: true,
  });

interface SpaceProp {
  id: number;
  name: string;
  posts_count: number;
  space_group: string;
  type: string;
  used_in_replies: number;
}

export type { SpaceProp };

export interface SpaceResponse {
  count: number;
  records: Array<SpaceProp>;
}

export const SPACES_FOR_KNOWLEDGE_QUERY_KEY = [
  "internal_api",
  "community_bot",
  "community_resources",
  "for_knowledge",
];

export const SPACES_FOR_IMPORT_QUERY_KEY = [
  "internal_api",
  "community_bot",
  "community_resources",
  "for_import",
];

export const useSpacesForKnowledge = ({
  currentPage,
  spaceIds,
}: {
  currentPage: number;
  spaceIds: number[];
}) =>
  useQuery<SpaceResponse>({
    queryKey: [...SPACES_FOR_KNOWLEDGE_QUERY_KEY, spaceIds],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.communityResources.index({
          params: {
            page: currentPage,
            per_page: 10,
            imported: "true",
            space_ids: spaceIds.join(","),
          },
        }),
      ),
    keepPreviousData: true,
  });

export const useSpacesForImport = ({
  currentPage,
  spaceIds,
  name,
  type,
  spaceGroup,
}: {
  currentPage: number;
  spaceIds: number[];
  name?: string;
  type?: string;
  spaceGroup?: string;
}) =>
  useQuery<SpaceResponse>({
    queryKey: [
      ...SPACES_FOR_IMPORT_QUERY_KEY,
      spaceIds,
      name,
      type,
      spaceGroup,
    ],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.communityResources.index({
          params: {
            page: currentPage,
            per_page: 10,
            imported: "false",
            space_ids: spaceIds.join(","),
            ...(name ? { name } : {}),
            ...(type ? { type } : {}),
            ...(spaceGroup ? { space_group: spaceGroup } : {}),
          },
        }),
      ),
    keepPreviousData: true,
  });
